import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import { ThumbsUp, ThumbsDown } from "lucide-react-native";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useSubstances } from "../SubstancesContext";
import { buildSubstancesNavigationStack } from "../buildSubstancesNavigationStack";
import type { SubstancesStackParamList } from "../SubstancesNavigator";

export function SubstancesIntroScreen() {
  const t = useTranslations();
  const { state, updateState } = useSubstances();
  const navigation = useNavigation<NavigationProp<SubstancesStackParamList>>();

  const selection = useMemo(() => {
    if (state.hasSubstances == null) return null;
    return state.hasSubstances ? "yes" : "no";
  }, [state.hasSubstances]);

  const syncNavigation = (partial: Partial<typeof state>) => {
    const merged = { ...state, ...partial };
    const stack = buildSubstancesNavigationStack(merged);
    const targetRoute = merged.hasSubstances
      ? "SubstancesSelect"
      : "SubstancesComplete";
    const targetIndex = stack.findIndex((route) => {
      if (typeof route === "string") return route === targetRoute;
      return false;
    });

    updateState({
      ...partial,
      navigationStack: stack,
      currentNavigationIndex:
        targetIndex >= 0 ? targetIndex : (merged.currentNavigationIndex ?? 0),
    });
  };

  const handleChange = (value: "yes" | "no") => {
    if (value === "yes") {
      syncNavigation({ hasSubstances: true });
    } else {
      const idsToDelete = Array.from(
        new Set(
          state.substanceIdsToDelete.concat(
            state.substanceDetails
              .map((detail) => detail.id)
              .filter((id): id is string => !!id),
          ),
        ),
      );

      syncNavigation({
        hasSubstances: false,
        selectedSubstances: [],
        substanceDetails: [],
        substanceIdsToDelete: idsToDelete,
      });
    }
  };

  const handleSave = () => {
    if (selection === "yes") {
      navigation.navigate("SubstancesSelect");
    } else if (selection === "no") {
      navigation.navigate("SubstancesComplete");
    }
  };

  return (
    <QuestionLayout
      title={t("med_history.lifestyle.substances.intro_title")}
      subtitle={t("med_history.lifestyle.substances.intro_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={!selection}
      saveLabel={t("med_history.lifestyle.continue")}
    >
      <View style={styles.optionsContainer}>
        <SquareOptionButton
          icon={ThumbsUp}
          label={t("med_history.shared.yes")}
          isSelected={selection === "yes"}
          onPress={() => handleChange("yes")}
          iconSize={24}
          containerSize={48}
          labelSize="lg"
        />
        <SquareOptionButton
          icon={ThumbsDown}
          label={t("med_history.shared.no")}
          isSelected={selection === "no"}
          onPress={() => handleChange("no")}
          iconSize={24}
          containerSize={48}
          labelSize="lg"
        />
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  optionsContainer: {
    flexDirection: "row",
    gap: 12,
    marginBottom: 40,
    width: "100%",
  },
});
